import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { useNavigate } from "react-router-dom";
import Layout from '../component/layout'
import { getCombinedInvoices } from '../../../utils/urls';
import { makeRequest2 } from '../../../utils/makeRequest';
import { useFacilityStore } from '../../../app/z-store/store';
import { getItem } from '../../../utils/localStorage';
import { Card, CardBody } from "reactstrap";

const CombinedInvoices = () => {
  const navigate = useNavigate();
  const toast = useRef(null);

  // State for combined invoices
  const [combinedInvoices, setCombinedInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [facilityId, setFacilityId] = useState(null);

  // Modal state
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(null); // 'reminder' or 'notification'
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const [communicationMethod, setCommunicationMethod] = useState("email"); // 'email', 'sms', or 'both'

  // Get facility ID from Zustand store
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // Year-month filter
  const [selectedYearMonth, setSelectedYearMonth] = useState("all");
  const [availableYearMonths, setAvailableYearMonths] = useState([
    "2024-01",
    "2024-02",
    "2024-03",
    "2024-04",
    "2024-05",
    "2024-06",
    "2024-07",
    "2024-08",
    "2024-09",
    "2024-10",
    "2024-11",
    "2024-12",
    "2025-01",
    "2025-02",
    "2025-03"
  ]);

  // Bulk action options
  const bulkActionOptions = [
    { label: "Send Bulk Reminders", value: "reminder", icon: "ti ti-bell" },
    { label: "Send Notifications", value: "notification", icon: "ti ti-mail" }
  ];

  // Format year-month for display
  const formatYearMonth = (yearMonth) => {
    if (!yearMonth) return "";
    const [year, month] = yearMonth.split("-");
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  const handleYearMonthChange = (yearMonth) => {
    setSelectedYearMonth(yearMonth);
  };

  // Handle bulk action selection
  const handleBulkActionSelect = (actionType) => {
    setBulkActionType(actionType);
    setSelectedPeriod("all");
    setCommunicationMethod("email");
    setShowBulkActionModal(true);
  };

  // Handle sending bulk action
  const handleSendBulkAction = () => {
    const actionLabel = bulkActionType === "reminder" ? "Reminders" : "Notifications";
    const methodLabel = communicationMethod === "both" 
      ? "Email & SMS" 
      : communicationMethod === "email" 
        ? "Email" 
        : "SMS";
    const periodLabel = selectedPeriod === "all" 
      ? "all periods" 
      : formatYearMonth(selectedPeriod);

    toast.current.show({
      severity: "success",
      summary: `${actionLabel} Sending`,
      detail: `Sending ${actionLabel.toLowerCase()} via ${methodLabel} for ${periodLabel}...`,
      life: 4000,
    });

    // Here you would make the API call to send the bulk action
    // Example:
    // await makeRequest2(`${sendBulkAction}`, "POST", {
    //   actionType: bulkActionType,
    //   period: selectedPeriod,
    //   method: communicationMethod,
    //   facilityId: facilityId
    // });

    setShowBulkActionModal(false);
  };

  // Fetch combined invoices
  const fetchCombinedInvoices = async (facilityId) => {
    if (!facilityId) {
      console.error("No facility ID provided");
      return;
    }

    setLoading(true);
    try {
      const response = await makeRequest2(`${getCombinedInvoices}/${facilityId}`, "GET");

      if (response.success) {
        // Map the response data to match the UI requirements
        const mappedInvoices = response.data.data.map(invoice => ({
          id: invoice._id,
          combinedInvoiceNumber: invoice.combinedInvoiceNumber || "N/A",
          customerName: invoice.customerInfo?.fullName ||
            `${invoice.customer.firstName} ${invoice.customer.lastName}`,
          phoneNumber: invoice.customer.phoneNumber || "N/A",
          units: invoice.unitInfo?.unitName || invoice.unit.name,
          period: invoice.period,
          totalAmount: invoice.totalAmount,
          status: invoice.status,
          issueDate: invoice.issueDate,
          dueDate: invoice.dueDate,
          invoiceCount: invoice.invoiceCount,
          invoiceTypes: invoice.invoiceTypes || [],
          amountPaid: invoice.amountPaid,
          calculatedBalance: invoice.calculatedBalance,
        }));

        setCombinedInvoices(mappedInvoices);
      } else {
        throw new Error(response.error || "Failed to fetch combined invoices");
      }
    } catch (error) {
      console.error("Error fetching combined invoices:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to fetch combined invoices",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value) => {
    return new Date(value).toLocaleDateString();
  };

  const formatCurrency = (amount) => `KES ${amount.toLocaleString()}`;

  const statusTemplate = (rowData) => {
    const getSeverity = (status) => {
      switch (status) {
        case "Paid":
          return { color: "#4caf50", bgColor: "#e8f5e9" };
        case "Partially Paid":
          return { color: "#ff9800", bgColor: "#fff3e0" };
        case "Unpaid":
          return { color: "#f44336", bgColor: "#ffebee" };
        case "Overdue":
          return { color: "#d32f2f", bgColor: "#ffcdd2" };
        default:
          return { color: "#2196f3", bgColor: "#e3f2fd" };
      }
    };

    const statusStyle = getSeverity(rowData.status);

    return (
      <span
        style={{
          fontWeight: "bold",
          color: statusStyle.color,
          padding: "5px 10px",
          borderRadius: "15px",
          backgroundColor: statusStyle.bgColor,
        }}
      >
        {rowData.status}
      </span>
    );
  };

  const actionTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="ti ti-eye"
          className="p-button-rounded p-button-success"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/facility/combined_invoices/view_combined_invoice/${rowData.id}`);
          }}
          tooltip="View Combined Invoice Details"
          tooltipOptions={{ position: 'top' }}
        />
        <Button
          icon="ti ti-download"
          className="p-button-rounded p-button-info"
          onClick={(e) => {
            e.stopPropagation();
            toast.current.show({
              severity: "info",
              summary: "Download",
              detail: `Downloading combined invoice ${rowData.combinedInvoiceNumber}`,
              life: 3000,
            });
          }}
          tooltip="Download PDF"
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  };

  const header = (
    <div className="mb-4 p-3 border rounded bg-white">
      <div className="row g-3 align-items-center">
        <div className="col-md-5">
          <div className="p-input-icon-left w-100">
            <i className="pi pi-search" />
            <InputText
              placeholder="Search by customer name, phone, invoice number, or unit..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="w-100"
              style={{ height: "40px", paddingLeft: "2.5rem" }}
            />
          </div>
        </div>
        <div className="col-md-4">
          <select
            className="form-control"
            value={selectedYearMonth}
            onChange={(e) => handleYearMonthChange(e.target.value)}
          >
            <option value="all">All Periods</option>
            {availableYearMonths.map((yearMonth) => (
              <option key={yearMonth} value={yearMonth}>
                {formatYearMonth(yearMonth)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <div className="d-flex gap-2">
            <Button
              label="Clear Filters"
              className="p-button-text btn btn-secondary"
              type="button"
              onClick={() => {
                setSelectedYearMonth("all");
                setSearchKeyword("");
              }}
            />
            <Button
              label="Generate Report"
              className="btn btn-primary"
              type="button"
              icon="ti ti-file-text"
              onClick={() => {
                toast.current.show({
                  severity: "info",
                  summary: "Report Generation",
                  detail: "Generating combined invoices report...",
                  life: 3000,
                });
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Filter combined invoices
  const filterInvoices = (invoices) => {
    return invoices.filter((invoice) => {
      const matchesSearch =
        invoice.customerName?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        invoice.phoneNumber?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        invoice.combinedInvoiceNumber?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        invoice.units?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        invoice.status?.toLowerCase().includes(searchKeyword.toLowerCase());

      const periodMatch = !selectedYearMonth || selectedYearMonth === "all" ||
        invoice.period === selectedYearMonth;

      return matchesSearch && periodMatch;
    });
  };

  const filteredInvoices = filterInvoices(combinedInvoices);

  // Calculate statistics
  const statistics = {
    paidInvoices: filteredInvoices.filter(inv => inv.status === "Paid").length,
    unpaidInvoices: filteredInvoices.filter(inv => inv.status === "Unpaid").length,
    partialInvoices: filteredInvoices.filter(inv => inv.status === "Partially Paid").length,
  };

  // Initialize facility ID and fetch data
  useEffect(() => {
    const initializeFacility = async () => {
      try {
        // Try to get from Zustand store first
        let currentFacilityId = selectedFacilityId;

        // If not in store, try localStorage
        if (!currentFacilityId) {
          currentFacilityId = await getItem('selectedFacilityId');
        }

        if (currentFacilityId) {
          setFacilityId(currentFacilityId);
          fetchCombinedInvoices(currentFacilityId);
        } else {
          toast.current.show({
            severity: "warn",
            summary: "Warning",
            detail: "No facility selected. Please select a facility.",
            life: 3000,
          });
        }
      } catch (error) {
        console.error("Error initializing facility:", error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to initialize facility",
          life: 3000,
        });
      }
    };

    initializeFacility();
  }, [selectedFacilityId]);

  // Bulk Action Modal Footer
  const bulkActionModalFooter = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={() => setShowBulkActionModal(false)}
        className="p-button-text"
      />
      <Button
        label="Send"
        icon="pi pi-send"
        onClick={handleSendBulkAction}
        className="p-button-primary"
        autoFocus
      />
    </div>
  );

  return (
    <Layout>
      <Toast ref={toast} />

      {/* Bulk Action Modal */}
      <Dialog
        header={bulkActionType === "reminder" ? "Send Bulk Reminders" : "Send Notifications"}
        visible={showBulkActionModal}
        style={{ width: '500px' }}
        onHide={() => setShowBulkActionModal(false)}
        footer={bulkActionModalFooter}
      >
        <div className="p-fluid">
          {/* Period Selection */}
          <div className="field mb-4">
            <label htmlFor="period" className="font-weight-bold mb-2">
              <i className="ti ti-calendar me-2"></i>
              Select Period
            </label>
            <select
              id="period"
              className="form-control"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
            >
              <option value="all">All Periods</option>
              {availableYearMonths.map((yearMonth) => (
                <option key={yearMonth} value={yearMonth}>
                  {formatYearMonth(yearMonth)}
                </option>
              ))}
            </select>
          </div>

          {/* Communication Method */}
          <div className="field">
            <label className="font-weight-bold mb-3">
              <i className="ti ti-message me-2"></i>
              Communication Method
            </label>
            <div className="d-flex flex-column gap-3">
              <div className="d-flex align-items-center">
                <RadioButton
                  inputId="email"
                  name="method"
                  value="email"
                  onChange={(e) => setCommunicationMethod(e.value)}
                  checked={communicationMethod === "email"}
                />
                <label htmlFor="email" className="ms-2 mb-0">
                  <i className="ti ti-mail me-2"></i>
                  Email Only
                </label>
              </div>
              <div className="d-flex align-items-center">
                <RadioButton
                  inputId="sms"
                  name="method"
                  value="sms"
                  onChange={(e) => setCommunicationMethod(e.value)}
                  checked={communicationMethod === "sms"}
                />
                <label htmlFor="sms" className="ms-2 mb-0">
                  <i className="ti ti-message-2 me-2"></i>
                  SMS Only
                </label>
              </div>
              <div className="d-flex align-items-center">
                <RadioButton
                  inputId="both"
                  name="method"
                  value="both"
                  onChange={(e) => setCommunicationMethod(e.value)}
                  checked={communicationMethod === "both"}
                />
                <label htmlFor="both" className="ms-2 mb-0">
                  <i className="ti ti-arrows-exchange me-2"></i>
                  Both Email & SMS
                </label>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="mt-4 p-3 bg-light rounded">
            <small className="text-muted">
              <strong>Summary:</strong> You are about to send{" "}
              {bulkActionType === "reminder" ? "reminders" : "notifications"} via{" "}
              {communicationMethod === "both" 
                ? "Email & SMS" 
                : communicationMethod === "email" 
                  ? "Email" 
                  : "SMS"}{" "}
              for{" "}
              {selectedPeriod === "all" 
                ? "all periods" 
                : formatYearMonth(selectedPeriod)}
              .
            </small>
          </div>
        </div>
      </Dialog>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="/app/">Dashboard</a>
                </li>
                <li className="breadcrumb-item" aria-current="page">
                  Combined Invoices
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-md-4">
          <Card>
            <CardBody className="text-center p-3">
              <i className="ti ti-circle-check text-success f-24 mb-2"></i>
              <h5 className="mb-0 text-success">{statistics.paidInvoices}</h5>
              <small className="text-muted">Paid Invoices</small>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-4">
          <Card>
            <CardBody className="text-center p-3">
              <i className="ti ti-clock text-warning f-24 mb-2"></i>
              <h5 className="mb-0 text-warning">{statistics.partialInvoices}</h5>
              <small className="text-muted">Partially Paid</small>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-4">
          <Card>
            <CardBody className="text-center p-3">
              <i className="ti ti-alert-circle text-danger f-24 mb-2"></i>
              <h5 className="mb-0 text-danger">{statistics.unpaidInvoices}</h5>
              <small className="text-muted">Unpaid Invoices</small>
            </CardBody>
          </Card>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">Combined Invoices</h5>
                <div className="d-flex gap-2">
                  <Button
                    label="Export to Excel"
                    className="btn btn-outline-success"
                    icon="ti ti-file-export"
                    onClick={() => {
                      toast.current.show({
                        severity: "info",
                        summary: "Export",
                        detail: "Exporting combined invoices to Excel...",
                        life: 3000,
                      });
                    }}
                  />
                  
                  {/* Bulk Actions Dropdown */}
                  <div className="dropdown">
                    <button
                      className="btn btn-primary dropdown-toggle"
                      type="button"
                      id="bulkActionsDropdown"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i className="ti ti-bolt me-2"></i>
                      Bulk Actions
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="bulkActionsDropdown">
                      {bulkActionOptions.map((option) => (
                        <li key={option.value}>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleBulkActionSelect(option.value)}
                          >
                            <i className={`${option.icon} me-2`}></i>
                            {option.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <DataTable
                value={filteredInvoices}
                header={header}
                loading={loading}
                paginator
                rows={10}
                emptyMessage="No combined invoices found"
                className="p-datatable-sm"
                stripedRows
                selectionMode="single"
                sortField="issueDate"
                sortOrder={-1}
                responsiveLayout="scroll"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} combined invoices"
                rowsPerPageOptions={[5, 10, 20, 50]}
              >
                <Column
                  field="combinedInvoiceNumber"
                  header="INV #"
                  style={{ minWidth: "180px" }}
                  sortable
                />
                <Column
                  field="customerName"
                  header="Tenant"
                  style={{ minWidth: "200px" }}
                  sortable
                />
                <Column
                  field="units"
                  header="Units"
                  style={{ minWidth: "150px" }}
                  body={(rowData) => rowData.units}
                />

                <Column
                  field="period"
                  header="Period"
                  style={{ minWidth: "120px" }}
                  body={(rowData) => formatYearMonth(rowData.period)}
                  sortable
                />
                <Column
                  field="totalAmount"
                  header="Total"
                  style={{ minWidth: "140px" }}
                  body={(rowData) => formatCurrency(rowData.totalAmount)}
                  sortable
                />
                <Column
                  field="status"
                  header="Status"
                  body={statusTemplate}
                  style={{ minWidth: "140px" }}
                  sortable
                />
                <Column
                  field="issueDate"
                  header="Issue Date"
                  body={(rowData) => formatDate(rowData.issueDate)}
                  style={{ minWidth: "120px" }}
                  sortable
                />
                <Column
                  header="Actions"
                  body={actionTemplate}
                  style={{ minWidth: "120px" }}
                />
              </DataTable>
            </CardBody>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default CombinedInvoices;